import * as React from "react";
import axios from "axios";
import { withFormik, FormikProps } from "formik";
import styled from "styled-components";
import { Trans, t } from "@lingui/macro";
import { I18n } from "@lingui/react";
import {
  spacing,
  DisplayMedium,
  DisplaySmall,
  Thirds,
  Center,
  Left,
  Right,
  Back,
  Button,
  ButtonGroup,
  FieldText,
  FieldTextarea,
  Field,
  Select,
} from "@conserveit/blocks";
import {
  ContentContainer,
  ContentFooter,
  ServerError,
  GoBack,
  EquipList,
  FieldLabel,
} from "./Action.styles";
import * as lists from "./utils/lists";
import {
  valuePair,
  selectChange,
  selectBlur,
  handleGoBack,
} from "./utils/controls";
import {
  formikOptions,
  addActionSchema,
  mapAddActionToProps,
} from "./utils/validation";
import { CreateFormValues, FromAssociation } from "./utils/types";

import FormikAttachmentField from "./components/FormikAttachmentField";
import { FormFieldGroup } from "./components/FormFieldGroup";
import { Expandable } from "./components/Expandable";

const StyledThirds = styled(Thirds)`
  margin-bottom: ${spacing.default};
`;

const SubHeading = styled(DisplaySmall)`
  margin-bottom: ${spacing.default};
`;

const Divider = styled.div`
  height: ${spacing.default};
`;

interface OwnProps {
  rules: string[];
  sites: Insite.Site[];
  userAccess: Insite.API.User["access"];
  userGroups: Insite.API.User["groups"];
  isPending: boolean;
  submitError: string;
  urlHistory: Insite.AppState["urlHistory"];
  onSave(values: CreateFormValues, equipList?: number[]): void;
  fromAssociate: FromAssociation;
  token: Insite.AppState["auth"]["user"]["token"];
}

type Props = OwnProps & FormikProps<CreateFormValues>;
const CreateAction: React.FunctionComponent<Props> = ({
  sites,
  userAccess,
  userGroups,
  isPending,
  submitError,
  fromAssociate,
  urlHistory,
  token,

  // FormikBag
  values,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldTouched,
  setFieldValue,
  touched,
}) => {
  const siteOptions = Object.values(sites).map((v) => ({
    label: v.label,
    value: v.id,
  }));

  const [assignees, setAssignees] = React.useState<Insite.Assignee[]>([]);

  React.useEffect(() => {
    if (values.site === "") return;

    const [projectKey, siteLabel] = values.site.split("~");
    axios
      .get(`/hashtagUsernamelist/${projectKey}/${siteLabel}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((result: { data: Insite.Assignee[] }) => {
        setAssignees(result.data);
      })
      .catch((error: any) => {
        console.error(error);
      });
    setFieldValue("assignee", "");
  }, [values.site]);

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <I18n>
        {({ i18n }) => (
          <React.Fragment>
            <StyledThirds>
              <Left>
                {/*
                  If user is coming from Equip view then back button should take them there
                  Otherwise, back to last Actions view state
                */}
                {fromAssociate === null ? (
                  <GoBack to={urlHistory.actionsHistory.path}>
                    <Back
                      text={
                        <Trans id="action_form.back_to_actions">Back</Trans>
                      }
                    />
                  </GoBack>
                ) : (
                  <Back
                    onClick={handleGoBack}
                    text={<Trans id="action_form.back_to_actions">Back</Trans>}
                  />
                )}
              </Left>
              <Center>
                <DisplayMedium>
                  <Trans id="action_form.add_action">Add Action</Trans>
                </DisplayMedium>
              </Center>
              <Right>
                <ButtonGroup tight>
                  <Button
                    primary
                    thin
                    type="submit"
                    disabled={isPending || !userAccess.includes("editIssue")}
                    data-cy="CreateAction-add-start"
                  >
                    <Trans id="action_form.add">Add</Trans>
                  </Button>
                </ButtonGroup>
              </Right>
            </StyledThirds>
            <ContentContainer>
              <FormFieldGroup
                heading={i18n._(
                  t("action_form.action_details_heading")`Action details`
                )}
              >
                <Field
                  label={<Trans id="action_form.site_label">Site</Trans>}
                  name="site"
                  required
                  help={
                    <Trans id="action_form.site_help">
                      Site that the Action will be associated with
                    </Trans>
                  }
                  error={touched.site && errors.site}
                >
                  {({ error, name, id }) => (
                    <Select
                      name={name}
                      id={id}
                      error={error}
                      className="t-siteSelect"
                      disabled={isPending}
                      value={siteOptions.find((s) => values.site === s.value)}
                      onChange={selectChange("site", setFieldValue)}
                      onBlur={selectBlur("site", setFieldTouched)}
                      options={siteOptions}
                    />
                  )}
                </Field>

                <FieldText
                  label={<Trans id="action_form.summary_label">Summary</Trans>}
                  name="summary"
                  help={
                    <Trans id="action_form.summary_help">
                      Summary of the problem this Action addresses
                    </Trans>
                  }
                  required
                  disabled={isPending}
                  value={values.summary}
                  error={touched.summary && errors.summary}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <FieldTextarea
                  rows={4}
                  label={
                    <Trans id="action_form.description_label">
                      Description
                    </Trans>
                  }
                  name="description"
                  placeholder={i18n._(
                    t(
                      "action_form.description_help"
                    )`More detailed explanation of the action...`
                  )}
                  disabled={isPending}
                  value={values.description}
                  error={touched.summary && errors.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <Field
                  required
                  name="status"
                  label={
                    <Trans id="action_form.priority_label">Priority</Trans>
                  }
                  error={touched.priority && errors.priority}
                >
                  {({ error, name, id }) => (
                    <Select
                      error={error}
                      name={name}
                      id={id}
                      className="t-prioritySelect"
                      value={lists
                        .getPriorityOptions(i18n)
                        .find((l) => l.value === values.priority)}
                      disabled={isPending}
                      onChange={selectChange("priority", setFieldValue)}
                      onBlur={selectBlur("priority", setFieldTouched)}
                      options={lists.getPriorityOptions(i18n)}
                    />
                  )}
                </Field>

                <Field
                  required
                  name="context"
                  label={<Trans id="action_form.context_label">Context</Trans>}
                  error={touched.context && errors.context}
                >
                  {({ error, name, id }) => (
                    <Select
                      error={error}
                      name={name}
                      id={id}
                      className="t-contextSelect"
                      value={lists
                        .getContextOptions(i18n)
                        .find((l) => l.value === values.context)}
                      disabled={isPending}
                      onChange={selectChange("context", setFieldValue)}
                      onBlur={selectBlur("context", setFieldTouched)}
                      options={lists.getContextOptions(i18n)}
                    />
                  )}
                </Field>
              </FormFieldGroup>

              <FormFieldGroup
                heading={i18n._(t("action_form.status_heading")`Status`)}
              >
                <Field
                  required
                  name="status"
                  label={
                    <Trans id="action_form.status_label">Action status</Trans>
                  }
                  error={touched.status && errors.status}
                >
                  {({ error, name, id }) => (
                    <Select
                      error={error}
                      name={name}
                      id={id}
                      className="t-statusSelect"
                      value={lists
                        .getStatusOptions(i18n)
                        .find((l) => l.value === values.status)}
                      disabled={isPending}
                      onChange={selectChange("status", setFieldValue)}
                      onBlur={selectBlur("status", setFieldTouched)}
                      options={lists.getStatusOptions(i18n)}
                    />
                  )}
                </Field>
              </FormFieldGroup>

              <FormFieldGroup
                heading={i18n._(
                  t("action_form.team_assignment_heading")`Team assignment`
                )}
              >
                <Field
                  required
                  name="responsible"
                  label={
                    <Trans id="action_form.responsible_label">
                      Responsible
                    </Trans>
                  }
                  error={touched.responsible && errors.responsible}
                >
                  {({ error, name, id }) => (
                    <Select
                      error={error}
                      name={name}
                      id={id}
                      className="t-responsibleSelect"
                      value={lists
                        .getResponsibleOptions(userGroups, i18n, values.site)
                        .find((l) => l.value === values.responsible)}
                      onChange={selectChange("responsible", setFieldValue)}
                      onBlur={selectBlur("responsible", setFieldTouched)}
                      options={lists.getResponsibleOptions(
                        userGroups,
                        i18n,
                        values.site
                      )}
                    />
                  )}
                </Field>

                <Field
                  name="assignee"
                  label={
                    <Trans id="action_form.assignee_label">Assignee</Trans>
                  }
                  error={touched.assignee && errors.assignee}
                >
                  {({ error, name, id }) => (
                    <Select
                      error={error}
                      name={name}
                      id={id}
                      isClearable
                      className="t-assigneeSelect"
                      value={valuePair(values.assignee)}
                      disabled={isPending}
                      onChange={selectChange("assignee", setFieldValue)}
                      onBlur={selectBlur("assignee", setFieldTouched)}
                      options={
                        assignees
                          ? assignees.map((a) => a.display).map(valuePair)
                          : []
                      }
                    />
                  )}
                </Field>
              </FormFieldGroup>

              {fromAssociate && (
                <FormFieldGroup
                  heading={i18n._(
                    t("action_form.rule_heading")`Rules and equips`
                  )}
                >
                  <FieldLabel>{values.rule}</FieldLabel>
                  <EquipList>
                    {fromAssociate.equips.map((h) => (
                      <li key={h.equipRuleId}>{h.equipName}</li>
                    ))}
                  </EquipList>
                </FormFieldGroup>
              )}

              <FormFieldGroup
                heading={i18n._(
                  t("action_form.resources_heading")`Other resources`
                )}
              >
                <FieldText
                  label={<Trans id="action_form.report_url">Report URL</Trans>}
                  name="skysparkLink"
                  disabled={isPending}
                  value={values.skysparkLink}
                  error={touched.skysparkLink && errors.skysparkLink}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <Trans render="label" id="action_form.attach_screenshot">
                  Attach screenshot
                </Trans>
                <FormikAttachmentField
                  name="screenshot"
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />
              </FormFieldGroup>

              <FormFieldGroup
                heading={i18n._(
                  t("action_form.estimated_savings_heading")`Estimated savings`
                )}
              >
                <Expandable visible={false}>
                  <SubHeading>
                    {i18n._(
                      t(
                        "action_form.estimated_savings_utility"
                      )`Utility savings`
                    )}
                  </SubHeading>

                  <FieldText
                    label={
                      <Trans id="action_form.electricity_label">
                        Electricity (kWh)
                      </Trans>
                    }
                    type="number"
                    name="elec"
                    help={
                      <Trans id="action_form.electricity_help">
                        Estimated annual loss
                      </Trans>
                    }
                    value={values.elec}
                    disabled={isPending}
                    error={touched.elec && errors.elec}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <FieldText
                    label={<Trans id="action_form.gas_label">Gas (GJ)</Trans>}
                    type="number"
                    name="gas"
                    help={
                      <Trans id="action_form.gas_help">
                        Estimated annual loss
                      </Trans>
                    }
                    value={values.gas}
                    disabled={isPending}
                    error={touched.gas && errors.gas}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <FieldText
                    label={
                      <Trans id="action_form.water_label">Water (kL)</Trans>
                    }
                    type="number"
                    name="water"
                    help={
                      <Trans id="action_form.water_help">
                        Estimated annual loss
                      </Trans>
                    }
                    value={values.water}
                    disabled={isPending}
                    error={touched.water && errors.water}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <Divider />
                  <SubHeading>
                    {i18n._(
                      t("action_form.estimated_savings_cost")`Cost savings`
                    )}
                  </SubHeading>
                  <FieldText
                    label={
                      <Trans id="action_form.savings_label">
                        Estimated energy savings ($)
                      </Trans>
                    }
                    type="number"
                    name="totalFinancialImpact"
                    help={
                      <Trans id="action_form.savings_help_energy">
                        Expected annual performance impact if the issue is not
                        resolved
                      </Trans>
                    }
                    value={values.totalFinancialImpact}
                    disabled={isPending}
                    error={
                      touched.totalFinancialImpact &&
                      errors.totalFinancialImpact
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <FieldText
                    label={
                      <Trans id="action_form.operational_savings_label">
                        Operational savings ($)
                      </Trans>
                    }
                    type="number"
                    name="operationalSavings"
                    help={
                      <Trans id="action_form.savings_help_operational">
                        Expected annual non-utility impact if the issue is not
                        resolved
                      </Trans>
                    }
                    value={values.operationalSavings}
                    disabled={isPending}
                    error={
                      touched.operationalSavings && errors.operationalSavings
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Expandable>
              </FormFieldGroup>

              {submitError !== "" && <ServerError>{submitError}</ServerError>}

              <ContentFooter>
                <ButtonGroup>
                  <Button
                    primary
                    type="submit"
                    disabled={isPending || !userAccess.includes("editIssue")}
                    data-cy="CreateAction-add-end"
                  >
                    <Trans id="action_form.add_action">Add Action</Trans>
                  </Button>
                </ButtonGroup>
              </ContentFooter>
            </ContentContainer>
          </React.Fragment>
        )}
      </I18n>
    </form>
  );
};

CreateAction.defaultProps = {
  submitError: "",
  fromAssociate: null,
};

const withForm = withFormik<OwnProps, CreateFormValues>({
  ...formikOptions,
  validationSchema: addActionSchema,
  // TODO: Should be ReturnType<typeof serialiseCreateAction> probably
  mapPropsToValues: mapAddActionToProps as any,
  handleSubmit: (values, { props }) => {
    const { fromAssociate, onSave } = props;

    const equipList = fromAssociate
      ? fromAssociate.equips.map((equip) => equip.equipRuleId)
      : undefined;

    onSave(values, equipList);
  },
})(CreateAction);

export { withForm as CreateAction };
